// ════════════════════════════════════════════════════════════════════
// LYRAEA — Sleeve.js   ·   procedural cover art
// ────────────────────────────────────────────────────────────────────
// Most tracks will never have cover art. Somebody records something at
// 2am and puts it up; asking them to also make a square image is exactly
// the kind of friction this app exists to remove. But a wall of empty
// grey boxes is what makes a library look unfinished, and a single shared
// placeholder makes every track look like the same track.
//
// So: every sleeve without art gets its own, generated from the id. Two
// hues off the Empire palette, a guilloche of arcs like the engraving on
// a banknote, a struck seal, and the corner cut every IE slab carries.
// Deterministic — the same track is the same sleeve forever, on every
// device, with nothing stored anywhere.
//
// SVG rather than canvas: it scales from a 34px row thumbnail to a 400px
// record sleeve with no blurring, no pixel ratio maths, no ref, and it
// costs nothing to render at any size.
// ════════════════════════════════════════════════════════════════════

import React, { useMemo } from "react";

// The palette a sleeve may draw from — the site's own accents. Deliberately
// NOT the full spectrum: a generated sleeve should always look like it
// belongs to the Empire, never like a random colour picker fired.
const HUES = [
  ["#d4a03c", "#f6dfa0"],   // gilt
  ["#7c3aed", "#a78bfa"],   // voidlight
  ["#c01427", "#ff3d52"],   // vermillion
  ["#5fa79f", "#9ed8d1"],   // canon
  ["#6b7391", "#a6adc4"],   // ashsteel
  ["#8a6a26", "#d4a03c"],   // worn gold
];

const GLYPHS = ["◆", "◈", "❖", "✦", "✧", "⬖", "⬗", "⧫", "◇", "❋", "✜", "⟡"];

// FNV-1a. Same family as the Mark Code's hash, for the same reason: cheap,
// synchronous, stable forever, and no dependency.
const hash = (s) => {
  let h = 0x811c9dc5;
  for (let i = 0; i < String(s).length; i++) {
    h = Math.imul(h ^ String(s).charCodeAt(i), 0x01000193) >>> 0;
  }
  return h;
};

// The two hues a given seed resolves to, exposed so surfaces AROUND a
// sleeve can borrow its colour — the now-playing sheet tints its ambient
// glow with this, which is what makes the screen feel like it belongs to
// the track rather than to the app.
export const sleeveHue = (seed = "") => {
  const h = hash(seed || "lyraea");
  const [dark, light] = HUES[h % HUES.length];
  return { dark, light };
};

export default function Sleeve({ seed = "", size = 46, title = "" }) {
  const art = useMemo(() => {
    const h = hash(seed || title || "lyraea");
    const [dark, light] = HUES[h % HUES.length];
    const [dark2] = HUES[(h >> 3) % HUES.length];
    return {
      dark, light, dark2,
      glyph: GLYPHS[(h >> 7) % GLYPHS.length],
      // Every sleeve is a different engraving: arc count, tilt and the
      // offset of the light source all fall out of the same hash.
      arcs: 3 + (h % 4),
      tilt: (h >> 11) % 360,
      cx: 28 + ((h >> 5) % 44),
      cy: 22 + ((h >> 9) % 40),
      band: 12 + ((h >> 13) % 22),
    };
  }, [seed, title]);

  const id = `sl${Math.abs(hash(seed || title)).toString(36)}`;

  return (
    <svg
      viewBox="0 0 100 100"
      width={size} height={size}
      style={{ display: "block", flex: "0 0 auto" }}
      aria-hidden="true"
    >
      <defs>
        <radialGradient id={`${id}g`} cx={`${art.cx}%`} cy={`${art.cy}%`} r="82%">
          <stop offset="0%"   stopColor={art.light} stopOpacity=".55" />
          <stop offset="42%"  stopColor={art.dark}  stopOpacity=".30" />
          <stop offset="100%" stopColor="#06060a"   stopOpacity="1" />
        </radialGradient>
        <linearGradient id={`${id}s`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"  stopColor={art.dark2} stopOpacity=".40" />
          <stop offset="60%" stopColor="#06060a"   stopOpacity="0" />
        </linearGradient>
        {/* the corner cut, as a clip rather than an overlay, so the art
            actually stops at the bevel instead of being covered by it */}
        <clipPath id={`${id}c`}>
          <path d="M0 0 H86 L100 14 V100 H14 L0 86 Z" />
        </clipPath>
      </defs>

      <g clipPath={`url(#${id}c)`}>
        <rect width="100" height="100" fill="#0b0910" />
        <rect width="100" height="100" fill={`url(#${id}g)`} />
        <rect width="100" height="100" fill={`url(#${id}s)`} />

        {/* the guilloche — concentric arcs, struck off-centre */}
        <g transform={`rotate(${art.tilt} 50 50)`} fill="none" stroke={art.light}>
          {Array.from({ length: art.arcs }).map((_, i) => (
            <circle
              key={i}
              cx={art.cx} cy={art.cy}
              r={art.band + i * (9 + (i % 3))}
              strokeWidth={i === 0 ? 0.9 : 0.45}
              opacity={0.5 - i * 0.07}
            />
          ))}
        </g>

        {/* a single ruled line, like the margin rule on a manuscript */}
        <line x1="0" y1="72" x2="100" y2="72" stroke={art.light} strokeWidth=".4" opacity=".22" />

        <text
          x="50" y="56" textAnchor="middle"
          fontSize="26" fill={art.light} opacity=".92"
          style={{ fontFamily: "var(--display)" }}
        >{art.glyph}</text>
      </g>

      {/* the bevel's own hairline, drawn on top so the edge reads as cut */}
      <path d="M0 0 H86 L100 14 V100 H14 L0 86 Z"
        fill="none" stroke={art.dark} strokeOpacity=".55" strokeWidth="1" />
    </svg>
  );
}
